// Design System
import { PrimaryButton } from 'jl-design-system/elements/button/Button';
import { Message } from 'jl-design-system/components/message/Message';

// Types
import { ClickAndCollectSearchMapViewProps } from './ClickAndCollectSearchMapView.types';

// Config
import getClickCollectFormattedTime from '@utils/helpers/time';

// Components
import ClickAndCollectDisplayOpeningHours from '@components/click-and-collect-display-opening-hours';
import Container from '@components/container';
import DisplayAddress from '@components/display-address/DisplayAddress';
import DistanceInMiles from '@components/distance-in-miles';
import GoogleMap from '@components/google-map/GoogleMap';
import Heading from '@components/heading';

// Styles
import styles from './click-and-collect-search-map-view.scss';

const ClickAndCollectSearchMapView = ({
  activeMapMarker = undefined,
  collectionPointSearchResults = [],
  disabled = false,
  getCollectionPointDatesApiCallActive = false,
  searchTerm = '',
  selectCollectionPoint = () => {},
  setActiveMapMarker = () => {},
}: ClickAndCollectSearchMapViewProps) => {
  const renderDisplayAddress = () => (
    <div>
      <div className={styles.displayAddress}>
        <DisplayAddress address={activeMapMarker?.address} inline />
      </div>

      <p>{activeMapMarker?.address?.phoneNumber}</p>
    </div>
  );

  const unavailabilityReason = activeMapMarker?.unavailabilityReason ?? '';
  const activeMapMarkerId = activeMapMarker?.id ?? '';
  const headingId = `click-collect-store-id-${activeMapMarkerId}`;

  return (
    <Container marginTop="2" width="full">
      {!!collectionPointSearchResults.length && (
        <GoogleMap
          activeMarker={activeMapMarker}
          className={styles.mapContainer}
          handleMarkerClick={(marker) => {
            setActiveMapMarker(marker);
          }}
          mapName="click-collect-search"
          markers={collectionPointSearchResults}
          searchTerm={searchTerm}
        />
      )}

      {activeMapMarker && (
        <>
          {!activeMapMarker.isAvailable && (
            <Container marginBottom="2" marginTop="3">
              <Message dataTestId="store-unavailable-message" title={unavailabilityReason} type="information" />
            </Container>
          )}
          <Container className={styles.activeMapMarkerContainer} marginTop="3">
            <div className={styles.infoContainer}>
              <div className={styles.storeNameDistanceContainer}>
                <Heading
                  className={styles.storeName}
                  id={headingId}
                  label={activeMapMarker.storeName}
                  tag="h5"
                  type="s"
                />
                <DistanceInMiles className={styles.distance} distanceInMiles={activeMapMarker.distanceInMiles} />
              </div>
              {activeMapMarker.isAvailable && (
                <p className={styles.timePriceInfo} data-testid="store-time-price-info">
                  Collect{' '}
                  {getClickCollectFormattedTime({
                    time: activeMapMarker.collectionTime,
                  })}{' '}
                  for <strong>{activeMapMarker.collectionChargeFormatted}</strong>
                </p>
              )}

              <div className={styles.infoContainerAddressContainer}>{renderDisplayAddress()}</div>
            </div>

            <div className={styles.addressContainer}>{renderDisplayAddress()}</div>

            {!!activeMapMarker?.openingHours?.length && (
              <div className={styles.openingHoursContainer}>
                <ClickAndCollectDisplayOpeningHours openingHours={activeMapMarker.openingHours} />
              </div>
            )}
            {activeMapMarker.isAvailable && (
              <div className={styles.buttonContainer}>
                <PrimaryButton
                  aria-describedby={headingId}
                  className={styles.selectButton}
                  disabled={disabled}
                  onClick={() => selectCollectionPoint({ collectionPoint: activeMapMarker })}
                  submitting={getCollectionPointDatesApiCallActive}
                >
                  Select shop
                </PrimaryButton>
              </div>
            )}
          </Container>
        </>
      )}
    </Container>
  );
};

export default ClickAndCollectSearchMapView;
